import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef
} from "react";
import {
  AButton,
  ACheckbox,
  ADivider,
  AIcon,
  AListItem,
  AListItemTitle,
  AMount
} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {DropdownMenu} from "components/DropdownMenu";
import {IncidentsContext} from "contexts/IncidentsContext";
import {selectIncidentsLoading} from "store/selectors";
import {ASSIGNMENT, STATUS} from "models/options";
import {pascalCase} from "utils/stringUtils";
import css from "./FilterMenuDropdown.module.scss";

const FilterMenuDropdown = () => {
  const {t} = useTranslation();
  const dropdownRef = useRef(null);
  const isLoading = useSelector(selectIncidentsLoading);

  const {filterSettings, handleFilterDropdownMenuChange} =
    useContext(IncidentsContext);

  const [settings, dispatchSettings] = useReducer(
    (state, action) => ({...state, ...action}),
    {
      assigneeFilter: filterSettings.assigneeFilter,
      statusFilter: filterSettings.statusFilter
    }
  );

  useEffect(() => {
    dispatchSettings(filterSettings);
  }, [filterSettings]);

  const handleAssigneeClicked = useCallback(
    (value) =>
      dispatchSettings({
        assigneeFilter: settings.assigneeFilter.includes(value)
          ? removeFilterSettings(settings.assigneeFilter, value)
          : addFilterSetting(settings.assigneeFilter, value)
      }),
    [settings.assigneeFilter]
  );

  const handleStatusClick = useCallback(
    (value) =>
      dispatchSettings({
        statusFilter: settings.statusFilter.includes(value)
          ? removeFilterSettings(settings.statusFilter, value)
          : addFilterSetting(settings.statusFilter, value)
      }),
    [settings.statusFilter]
  );

  const handleApplyClick = useCallback(() => {
    handleFilterDropdownMenuChange({...settings});
    dropdownRef.current.closeMenu();
  }, [settings]);

  const handleCancelClick = useCallback(() => {
    dispatchSettings({...filterSettings});
    dropdownRef.current.closeMenu();
  }, [filterSettings]);

  const addFilterSetting = (filterSettings, value) =>
    [...filterSettings, value].sort();

  const removeFilterSettings = (filterSettings, value) =>
    filterSettings.filter((setting) => setting !== value);

  return (
    <AMount style={{position: "relative"}}>
      <DropdownMenu
        data-testid={"FilterMenuDropdown-DropdownMenu"}
        ref={dropdownRef}
        className="incidents-filter"
        buttonContent={
          <>
            <AIcon left size={12}>
              funnel-simple
            </AIcon>
            <span>Filters</span>
          </>
        }
        buttonClass="incidents-filter__btn ml-2 align-self-center"
        buttonProps={{
          disabled: isLoading,
          medium: true
        }}
        buttonSpanProps={{className: css.btnSpan}}
        chevron={false}
        menuClass={classNames("incidents-filter__menu", css.menu, "pt-1")}>
        <AListItemTitle className={css.listItemTitle}>
          {t("incidents.filters.assignmentPrefix")}
        </AListItemTitle>
        {ASSIGNMENT.map((assignment) => (
          <AListItem
            key={`assignment-${assignment.value}`}
            onClick={(e) => handleAssigneeClicked(assignment.value, e)}>
            <ACheckbox
              className="incidents-filter__assignee"
              checked={settings.assigneeFilter.includes(assignment.value)}
              disabled={isLoading}
              onClick={(e) => e.stopPropagation()}>
              {assignment.name}
            </ACheckbox>
          </AListItem>
        ))}
        <AListItemTitle className={css.listItemTitle}>
          {t("incidents.filters.statusPrefix")}
        </AListItemTitle>
        {STATUS.map((status) => (
          <AListItem
            key={`status-${status.value}`}
            onClick={(e) => handleStatusClick(pascalCase(status.value), e)}>
            <ACheckbox
              className="incidents-filter__status"
              checked={settings.statusFilter.includes(pascalCase(status.value))}
              disabled={isLoading}
              onClick={(e) => e.stopPropagation()}>
              {status.name}
            </ACheckbox>
          </AListItem>
        ))}
        <ADivider className={(css.divider, "ma-0")} />
        <div
          className={classNames(
            css.buttonGroup,
            "button-group d-flex justify-end"
          )}>
          <AButton
            data-testid={"FilterMenuDropdown-Button-cancel"}
            medium
            secondary
            className="incidents-filter__btn-cancel mr-2"
            onClick={handleCancelClick}>
            Cancel
          </AButton>
          <AButton
            data-testid={"FilterMenuDropdown-Button-apply"}
            medium
            primary
            className="incidents-filter__btn-submit"
            onClick={handleApplyClick}>
            Apply
          </AButton>
        </div>
      </DropdownMenu>
    </AMount>
  );
};

export default FilterMenuDropdown;
